<template>
  <section
    :class="[
      'real-time-table-cell',
      { arrival: columnName === 'arrival' },
      { 'arrival-badges-column': columnName === 'arrival_badges' },
      { 'environment-cell': isEnvironmentCell },
    ]"
    :style="{ paddingInlineStart: isSingleIconCell ? '15px' : '' }"
  >
    <template v-if="isEnvironmentCell">
      {{ formatEnvironmentValue(slaData[columnName], columnName) }}
    </template>
    <template v-else-if="isStringCell">
      <span
        v-if="columnName === 'name'"
        class="cell-text cell-text--name"
      >{{ $t(strToShow) }}</span>
      <template v-else>{{ $t(strToShow) }}</template>
    </template>
    <template v-if="columnName === 'date'">{{ ddmmyyhhmm(slaData.date) }}</template>
    <template v-else-if="columnName === 'arrival' && !isMaintenance">
      <span class="arrival-time-only">{{ slaData.arrival ? formatTime(slaData.arrival.time) : '—' }}</span>
    </template>
    <template v-else-if="columnName === 'arrival_badges' && !isMaintenance">
      <div class="arrival-badges">
        <Tooltip
          v-if="slaData.arrival"
          :text="`${$t('cleaner_arrivals')}: ${slaData.arrival.count}`"
          style="display: inline-flex"
        >
          <span class="arrival-count arrival-count--green">{{ slaData.arrival.count }}</span>
        </Tooltip>
      </div>
    </template>
    <template v-else-if="columnName === 'visits_since_cleaned'">
      <Tooltip
        v-if="slaData.pastVisits != null && slaData.pastVisits > 0"
        :text="$t('visits_since_cleaned')"
        style="display: inline-flex"
      >
        <span class="visits-count">{{ slaData.pastVisits }}</span>
      </Tooltip>
      <span v-else class="visits-count visits-count--empty">—</span>
    </template>
    <template v-else-if="columnName === 'last_arrival'">
      {{ formatLastArrival(slaData.last_arrival) }}
    </template>
    <template v-else-if="isSingleIconCell">
      <div v-if="columnName === 'cleandex' && slaData.debug" class="cleandex-cell">
        <Tooltip
          :text="
            `Visits: Past - ${slaData.debug.pastVisits} Future - ${slaData.debug.futureVisits}
            Complaints Rate: ${slaData.debug.complaintsVisitsRate != null ? Number(slaData.debug.complaintsVisitsRate).toFixed(5) : '—'}
            Weighted Cleandex: ${slaData.cleandex} ${slaData.debug.complaints ? `
            Open Complaints Count: ${slaData.debug.complaints}` : ``}`
          "
          class="cleandex-cell-tooltip"
        >
          <img :src="cleandexIcon" />
        </Tooltip>
      </div>
      <span v-else-if="columnName === 'cleandex'">—</span>
      <Tooltip
        v-else-if="columnName === 'sla_status' && slaStatusTooltip"
        :text="$t(slaStatusTooltip)"
        style="display: inline-flex"
      >
        <img :src="iconToShow" />
      </Tooltip>
      <img v-else-if="columnName !== 'gender' || iconToShow" :src="iconToShow" />
    </template>
    <div v-else-if="columnName === 'title'" class="title-cell">
      <div v-if="slaData.title && slaData.categoryColor" class="title-container" :title="slaData.title + (slaData.category ? ' — ' + slaData.category : '')">
        <span class="title-category-bar" :style="{ backgroundColor: slaData.categoryColor }"></span>
        <span class="title-text">{{ slaData.title }}</span>
      </div>
    </div>
    <div class="call-cell-container" v-else-if="columnName === 'call'">
      <div v-for="(complaint, index) in slaData.complaints" :key="index" class="call-icon-container">
        <Tooltip :boldText="complaint.name" :text="formatTime(complaint.time)" style="display:flex">
          <span :style="getComplaintCountStyle(complaint)" v-if="complaint.count > 1">{{ complaint.count }}</span>
          <img :style="getComplaintStyle(complaint)" :src="getServikIcon(complaint)" />
        </Tooltip>
      </div>
    </div>
  </section>
</template>

<script>
import Tooltip from '../../common/components/Tooltip'
import realTimeService from '../services/realTimeService'
import realTimeClock from '../services/realTimeClock'
import utilService from '../../common/services/utilService'
import { isTabletDevice, getRealTimeTabletStatusDisplay } from '../utils/deviceConnectivityUtils'
import { ddmmyyhhmm } from '../../../filters'

/**@typedef {{
 * name: String;
 * iconUrl: String;
 * time: String | Number;
 * category: String;
 * count: Number;
 * isSuggestion: boolean;
 * }} Complaint*/

export default {
  props: {
    columnName: {
      type: String,
      required: true,
      validator: (prop) =>
        [
          'name',
          'building',
          'humidity',
          'temperature',
          'zone',
          'floor',
          'gender',
          'arrival',
          'arrival_badges',
          'visits_since_cleaned',
          'time_start',
          'sla_status',
          'duration',
          'call',
          'date',
          'title',
          'cleandex',
          'last_arrival',
          'assignee',
        ].includes(prop),
    },
    /** @type {new() {
     * name: String;
     * building: String;
     * zone: String;
     * floor: String;
     * gender: String;
     * isOpen: Boolean;
     * openTime: String | Number;
     * complaints: Complaint[]
     * }} */
    slaData: { type: Object, required: true },
    //max sla in minutes of current shift to calculate countdown(duration)
    maxSla: { type: Number, required: false },
    isMaintenance: { type: Boolean, default: false, required: false },
    /** Company GMT offset for displaying times in company timezone (e.g. 8 for GMT+8). */
    companyGmt: { type: [Number, String], default: null, required: false },
  },
  components: {
    Tooltip,
  },
  methods: {
    companyGmtValue() {
      return this.companyGmt != null && this.companyGmt !== '' ? this.companyGmt : null
    },
    /** HH:MM in company timezone only. No local time fallback. */
    formatTime(isoTime) {
      return utilService.formatHMInCompanyTz(isoTime, this.companyGmtValue())
    },
    /** Date + time for last_arrival in company timezone only. No local time fallback. */
    formatLastArrival(isoTime) {
      const date = new Date(isoTime);
      if (isNaN(date.getTime())) return '--';
      const tz = utilService.timeZoneForGmt(this.companyGmtValue());
      if (tz) {
        const day = date.toLocaleString('en-GB', { timeZone: tz, day: '2-digit' });
        const month = date.toLocaleString('en-GB', { timeZone: tz, month: '2-digit' });
        const year = date.toLocaleString('en-GB', { timeZone: tz, year: '2-digit' });
        const hour = date.toLocaleString('en-GB', { timeZone: tz, hour: '2-digit' });
        const minute = date.toLocaleString('en-GB', { timeZone: tz, minute: '2-digit' });
        return `${day}/${month}/${year} ${hour}:${minute}`;
      }
      return '--';
    },
    /** Date+time for time_start column in company timezone only. No local time fallback. Hour/minute with leading zeros. */
    formatTimeStart(isoTime) {
      const date = new Date(isoTime);
      if (isNaN(date.getTime())) return '--';
      const tz = utilService.timeZoneForGmt(this.companyGmtValue());
      if (tz) {
        const d = date.toLocaleString('en-GB', { timeZone: tz, day: 'numeric' });
        const m = date.toLocaleString('en-GB', { timeZone: tz, month: 'numeric' });
        const y = date.toLocaleString('en-GB', { timeZone: tz, year: 'numeric' }).slice(2, 4);
        const hour = date.toLocaleString('en-GB', { timeZone: tz, hour: 'numeric', hour12: false });
        const minute = date.toLocaleString('en-GB', { timeZone: tz, minute: 'numeric' });
        const h = String(Number(hour)).padStart(2, '0');
        const min = String(Number(minute)).padStart(2, '0');
        return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y} ${h}:${min}`;
      }
      return '--';
    },
    getServikIcon({ name, category, id, iconUrl }) {
      let icon
      try {
        if (iconUrl) icon = iconUrl
        else icon = require(`@/assets/survey/servik_icons_white/${category}/${id}.${name}.svg`)
      } catch (err) {
        icon = null
      }
      return icon
    },
    getComplaintCountStyle() {
      return {
        borderColor: this.isMaintenance ? '#389CEB' : '#fe5060',
        color: this.isMaintenance ? '#389CEB' : '#fe5060',
      }
    },
    getComplaintStyle({ id: servikId, isSuggestion, isOffline, backgroundColor }) {
      if (servikId === 216 || servikId === 1008) return { backgroundColor: '#AE7EA3', borderColor: '#AE7EA3' }
      else if (isSuggestion) return { backgroundColor: '#DD7ADD', borderColor: '#DD7ADD' }
      else if (isOffline) return { backgroundColor: '#808080', borderColor: '#808080' }
      else if (backgroundColor) {
        return {
          backgroundColor,
          borderColor: backgroundColor
        }
      } else {
        return {
          backgroundColor: this.isMaintenance ? '#389CEB' : '#fe5060',
          borderColor: this.isMaintenance ? '#389CEB' : '#fe5060',
        }
      }
    },
    ddmmyyhhmm(date) {
      return ddmmyyhhmm(date)
    },
    getSideLabelStyle(color) {
      return `border-left: 7px solid ${color}; float:left; width: initial; padding-left: 5px;`
    },
    formatEnvironmentValue(value, columnName) {
      if (value === null || typeof value === 'undefined' || value === '') return '—'
      const parsed = Number(value)
      if (isNaN(parsed)) return '—'
      const unit = columnName === 'humidity' ? '%' : 'C'
      return `${parsed.toFixed(2)}${unit}`
    },
  },
  computed: {
    /** Milliseconds since an open SLA was opened, ticking with the shared clock; null when not running. */
    openElapsedMs() {
      if (!this.slaData.isOpen || !this.slaData.openTime) return null
      return realTimeClock.now - new Date(this.slaData.openTime).getTime()
    },
    strToShow() {
      if (this.columnName === 'duration') {
        if (this.slaData.responseTime) return realTimeService.formatElapsed(this.slaData.responseTime)
        if (this.slaData.isOpen) return this.openElapsedMs == null ? '00:00:00' : realTimeService.formatElapsed(this.openElapsedMs)
      } else if (this.columnName === 'time_start' && this.slaData.openTime && this.slaData.isOpen)
        return this.formatTimeStart(this.slaData.openTime)
      else if (this.columnName === 'assignee')
        return this.slaData.assigneeName || ''
      return this.slaData[this.columnName]
    },
    isStringCell() {
      return ['name', 'building', 'zone', 'floor', 'time_start', 'duration', 'assignee'].includes(this.columnName)
    },
    isEnvironmentCell() {
      return ['humidity', 'temperature'].includes(this.columnName)
    },
    isSingleIconCell() {
      return ['gender', 'sla_status', 'cleandex'].includes(this.columnName)
    },
    cleandexIcon() {
      const complaints = this.slaData && this.slaData.complaints
      const callOpen = Boolean(this.slaData && this.slaData.cleandexCallOpen)
        || (Array.isArray(complaints) && complaints.some((item) => Number(item.servikId || item.servik_id) === 1053))
      const callDue = Boolean(this.slaData && this.slaData.cleandexCallDue)
      if (callOpen || callDue) return require('@/assets/performance/warning.svg')
      return require('@/assets/real_time_icons/OK.svg')
    },
    iconToShow() {
      let icon
      try {
        const { sensorState, sensorType, isOpen, date } = this.slaData
        const isPad = sensorType === 'pad9' || sensorType === 'pad9+'
        const tabletDisplay = isTabletDevice(sensorType)
          ? getRealTimeTabletStatusDisplay(this.slaData)
          : null
        if (this.columnName === 'gender') icon = this.slaData.genderIcon || null
        else if (this.columnName === 'sla_status') {
          if (tabletDisplay) {
            icon = tabletDisplay.iconAsset
          } else {
            let minPassed
            if (date) {
              if (new Date(date).getTime() > realTimeClock.now) return null
              if (this.openElapsedMs != null) minPassed = Math.floor(this.openElapsedMs / (1000 * 60))
            }
            if (!isOpen) icon = require('@/assets/real_time_icons/OK.svg')
            else if (this.maxSla && minPassed > this.maxSla) icon = require('@/assets/performance/warning.svg')
            else icon = require('@/assets/real_time_icons/warning_orange.svg')
            if (isPad) {
              if (!sensorState || sensorState === 'offline') icon = require('@/assets/offline.svg')
            }
          }
        }
      } catch (err) {
        icon = null
      }
      return icon
    },
    /** Tooltip key for device status icon (sla_status column). */
    slaStatusTooltip() {
      if (this.columnName !== 'sla_status') return null
      const { sensorState, sensorType } = this.slaData
      const isPad = sensorType === 'pad9' || sensorType === 'pad9+'
      if (isPad) {
        if (!sensorState || sensorState === 'offline') return 'offline'
        return 'online'
      }
      if (isTabletDevice(sensorType)) {
        return getRealTimeTabletStatusDisplay(this.slaData).tooltipKey
      }
      return 'online'
    },
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

$real-time-red: #fe5060;

.cell-text--ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.real-time-table-cell.environment-cell {
  text-align: center;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1.2;
}

.cell-text--name {
  display: block;
  min-width: 0;
  word-break: break-word;
  line-height: 1.3;
}

.arrival .arrival-time-only {
  font-variant-numeric: tabular-nums;
  color: inherit;
}

.arrival-badges-column .arrival-badges,
.arrival-badges {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.arrival-count {
  border-radius: 50%;
  @include flex-center;
  width: 22px;
  height: 22px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: default;
  flex-shrink: 0;
  @media (width: 1280px) {
    height: 38px;
    width: 38px;
    font-size: 0.9rem;
  }

  &--green {
    border: 1px solid #5f9b46;
    background-color: rgba(95, 155, 70, 0.2);
    color: #5f9b46;
  }
}

/* "Visits since last cleaned" – number in a circle (distinct from green arrival circle).
   Current: teal. Alternatives: blue #2563eb / #1e40af | indigo #6366f1 / #4338ca | amber #d97706 / #b45309 */
.visits-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  font-size: 0.8rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #0f766e;
  background: rgba(20, 184, 166, 0.18);
  border: 1.5px solid #14b8a6;
  border-radius: 50%;
  cursor: default;
  flex-shrink: 0;

  &--empty {
    font-weight: 400;
    font-size: 0.85rem;
    color: #94a3b8;
    background: transparent;
    border: 1px dashed #cbd5e1;
  }
}

.cleandex-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;

  ::v-deep .cleandex-cell-tooltip {
    display: inline-flex;
    align-items: center;
  }
}

.title-cell {
  min-width: 0;
  overflow: hidden;
}

.title-container {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  cursor: default;
}

.title-category-bar {
  flex-shrink: 0;
  width: 4px;
  min-height: 1.4em;
  border-radius: 2px;
  align-self: stretch;
}

.title-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  line-height: 1.3;
}

.call-cell-container {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  .call-icon-container {
    display: inline-flex;
    flex-shrink: 0;
    span {
      align-self: flex-end;
      position: relative;
      right: -8px;
      @include rtl {
        right: unset;
        left: -8px;
      }
      bottom: -5px;
      border: 1px solid $real-time-red;
      color: $red;
      font-weight: $font-weight-bold;
      border-radius: 50%;
      height: 15px;
      width: 15px;
      @include flex-center;
      font-size: 9px;
      background-color: $white;
    }

    img {
      height: 25px;
      width: 25px;
      border-radius: 25%;
      background-color: $real-time-red;
      border: 1px solid $real-time-red;
      padding: 3px;
      margin-inline-end: 2px;
      @media (min-width: 1280px) {
        height: 25px;
        width: 25px;
      }
    }
  }
}
</style>
